import request from './request'

/** list / meta 均未返回标签时的兜底（数量仍来自 list 接口 order_stats） */
export const DEFAULT_ORDER_STAT_LABELS: Record<string, string> = {
  hmc_orders: 'HMC Orders',
  fu_orders: 'FU Orders',
  ad_orders: 'AD Orders',
  hmc: 'HMC Orders',
  fu: 'FU Orders',
  ad: 'AD Orders',
}

export interface OrderStats {
  hmc_orders?: number
  fu_orders?: number
  ad_orders?: number
  hmc_orders_label?: string
  fu_orders_label?: string
  ad_orders_label?: string
  hmc_orders_display?: string
  fu_orders_display?: string
  ad_orders_display?: string
  items?: TechnicianOrderStatItem[]
  [key: string]: unknown
}

export interface TechnicianOrderStatItem {
  label?: string
  count?: number
  display?: string
  text?: string
  key?: string
  stat_key?: string
}

export interface UpcomingService {
  order_id?: string
  plan_key?: string
  plan_label?: string
  label?: string
  name?: string
  title?: string
  service_name?: string
  date?: string
  time_start?: string
  time_end?: string
  display?: string
  [key: string]: unknown
}

export interface TechnicianListItem {
  id: number
  technician_no: string
  name: string
  avatar: string
  phone_code: string
  phone: string
  mobile: string
  work_role: string
  work_role_text?: string
  license_no?: string
  sin_type?: string
  sin_number?: string
  residency_status?: string
  address?: string
  email?: string
  settlement_config?: Record<string, unknown>
  status?: number
  status_text?: string
  order_stats?: OrderStats | TechnicianOrderStatItem[]
  order_stat_items?: TechnicianOrderStatItem[]
  no_orders_text?: string
  upcoming_service?: UpcomingService | string | null
  upcoming_none_text?: string
  upcoming_plan_label?: string
  upcoming_display?: string
  create_time?: string
  update_time?: string
}

export interface Pagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface TechnicianListData {
  list: TechnicianListItem[]
  pagination: Pagination
}

export interface TechnicianListParams {
  page: number
  page_size: number
  search_type?: string
  keyword?: string
  /** 1 = Active（与接口文档一致） */
  status?: number
  work_role?: string
}

/** POST `/api/admin/technician/meta` 字典/筛选项（Apipost） */
export interface TechnicianMetaData {
  work_roles?: Array<{ value: string; label: string } | string>
  sin_types?: Array<{ value: string; label: string } | string>
  settlement_types?: Array<{ value: string; label: string } | string>
  required_settlement_keys?: Record<string, unknown>
  search_types?: Array<{ value: string; label: string } | string>
  order_stat_labels?: Record<string, string>
  order_stat_types?: Array<{ value: string; label: string } | string>
  order_types?: Array<{ value: string; label: string } | string>
}

/** 接口文档：Active 检测员/技术员 */
export const TECHNICIAN_LIST_STATUS_ACTIVE = 1

export interface TechnicianSearchTypeOption {
  value: string
  label: string
}

/** 搜索框内展示的短标签（下拉选项仍用完整 label） */
export function getTechnicianSearchTypeDisplayLabel(
  option: Pick<TechnicianSearchTypeOption, 'value' | 'label'>,
): string {
  if (option.value === 'mobile_no') return 'Mobile No.'
  return option.label
}

/** meta 未返回 search_types 时的后备搜索类型 */
export const DEFAULT_TECHNICIAN_SEARCH_TYPES: TechnicianSearchTypeOption[] = [
  { value: 'mobile_no', label: 'Registered Mobile No.' },
  { value: 'technician_id', label: 'Technician ID' },
  { value: 'name', label: 'Name' },
]

function normalizeTechnicianSearchTypeOption(
  entry: { value: string; label: string } | string,
): TechnicianSearchTypeOption | null {
  if (typeof entry === 'string') {
    const value = entry.trim()
    if (!value) return null
    const fallback = DEFAULT_TECHNICIAN_SEARCH_TYPES.find((o) => o.value === value)
    return { value, label: fallback?.label ?? value }
  }
  const value = String(entry.value ?? '').trim()
  const label = String(entry.label ?? '').trim()
  if (!value) return null
  return { value, label: label || value }
}

/** 搜索下拉：优先 meta.search_types，否则用 DEFAULT_TECHNICIAN_SEARCH_TYPES */
export function buildTechnicianSearchTypes(
  meta: TechnicianMetaData | null | undefined,
): TechnicianSearchTypeOption[] {
  const raw = meta?.search_types
  if (!Array.isArray(raw) || !raw.length) {
    return [...DEFAULT_TECHNICIAN_SEARCH_TYPES]
  }
  const options = raw
    .map(normalizeTechnicianSearchTypeOption)
    .filter((o): o is TechnicianSearchTypeOption => o != null)
  return options.length ? options : [...DEFAULT_TECHNICIAN_SEARCH_TYPES]
}

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

/** POST `/api/admin/technician/meta` — 获取字典（Apipost） */
export async function postTechnicianMeta(
  params: Partial<TechnicianListParams> = {},
): Promise<ApiResponse<TechnicianMetaData>> {
  return request.post<ApiResponse<TechnicianMetaData>>('/api/admin/technician/meta', params)
}

/** POST `/api/admin/technician/list` — 列表与搜索（Header `token` 由 request 拦截器注入） */
export async function getTechnicianList(params: TechnicianListParams) {
  return request.post<ApiResponse<TechnicianListData>>('/api/admin/technician/list', params)
}

/**
 * 技术人员搜索：每次点击 Search / 翻页均 POST `/api/admin/technician/list`，
 * 由后端按 search_type + keyword 过滤并返回 list + pagination。
 */
export async function searchTechnicianList(params: TechnicianListParams) {
  return getTechnicianList(params)
}

export interface TechnicianListAllPagesResult {
  list: TechnicianListItem[]
  total: number
  totalPages: number
}

/** 分页拉取全部技术员 */
export async function fetchTechnicianListAllPages(
  opts?: Omit<TechnicianListParams, 'page' | 'page_size'>,
): Promise<TechnicianListAllPagesResult> {
  const pageSize = 100
  const merged: TechnicianListItem[] = []
  let page = 1
  let totalPages = 1
  let total = 0

  for (;;) {
    const response = await getTechnicianList({
      page,
      page_size: pageSize,
      ...opts,
    })
    if (response.code !== 200) break
    const list = response.data?.list ?? []
    const pagination = response.data?.pagination
    if (page === 1) {
      total = pagination?.total ?? 0
    }
    merged.push(...list)
    totalPages = pagination?.total_pages ?? 1
    if (page >= totalPages || list.length < pageSize) break
    page += 1
  }

  return { list: merged, total, totalPages }
}

/** 分页拉取全部 Active 技术员（Schedule / 订单列表 lookup 用） */
export async function fetchActiveTechnicianList(
  opts?: Omit<TechnicianListParams, 'page' | 'page_size' | 'status'>,
): Promise<TechnicianListItem[]> {
  const pageSize = 100
  const merged: TechnicianListItem[] = []
  let page = 1
  let totalPages = 1

  for (;;) {
    const response = await getTechnicianList({
      status: TECHNICIAN_LIST_STATUS_ACTIVE,
      page,
      page_size: pageSize,
      ...opts,
    })
    if (response.code !== 200) break
    const list = response.data?.list ?? []
    merged.push(...list)
    totalPages = response.data?.pagination?.total_pages ?? 1
    if (page >= totalPages || list.length < pageSize) break
    page += 1
  }

  return merged
}
